// "Tus formatos" en el selector de formato: los formatos personalizados de la marca y el
// alta de uno nuevo — ADR-2026-10-20-formatos-personalizados.
//
// Un formato personalizado tiene las mismas opciones que "Formato de descarga" de
// Funciones avanzadas (es el mismo formato, la misma lista): nombre, ancho, alto, tipo de
// archivo y peso máximo. Se genera en la proporción estándar más parecida y el backend lo
// recorta a la medida exacta antes de guardarlo; el tipo de archivo y el peso son de la
// descarga (ADR-2026-10-21-el-recorte-al-formato-personalizado-es-del-backend).

import React, { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import {
  ANCHO_MAX,
  ANCHO_MIN,
  PESO_MAX_KB,
  PESO_MIN_KB,
  nuevoPerfil,
  type FormatoDeSalida,
} from '../../../utils/configDeDescarga';
import { etiquetaDeFormatoPersonalizado, slugDeFormatoPersonalizado } from '../../../utils/formatoPersonalizado';
import { agregarFormatoPersonalizado, useFormatosPersonalizados } from '../../../utils/formatosPersonalizadosStore';

const ARCHIVOS: Array<{ id: FormatoDeSalida; nombre: string }> = [
  { id: 'jpg', nombre: 'JPG' },
  { id: 'png', nombre: 'PNG' },
  { id: 'webp', nombre: 'WebP' },
];

const ROTULO = 'block text-[12px] font-medium text-[#3C3C43]/70 mb-1';
const CAMPO =
  'w-full h-9 px-3 rounded-lg border border-neutral-200 bg-white text-[13.5px] text-brand-black focus:outline-none focus:border-brand-primary';

/** El rectángulo con la proporción del formato, en lugar de una foto de ejemplo. */
const Proporcion: React.FC<{ ancho: number; alto: number; lado: number }> = ({ ancho, alto, lado }) => {
  const escala = lado / Math.max(ancho, alto);
  return (
    <span className="flex items-center justify-center bg-neutral-100" style={{ width: lado + 16, height: lado + 16 }}>
      <span
        className="rounded-[3px] border-2 border-brand-primary/60 bg-white"
        style={{ width: Math.max(8, ancho * escala), height: Math.max(8, alto * escala) }}
      />
    </span>
  );
};

export const FormatosPersonalizados: React.FC<{
  selectedId: string | null;
  onPick: (slug: string) => void;
  /** `chips`: pastillas, como el resto de los formatos en la Configuración de Crear. */
  layout: 'grid' | 'rows' | 'chips';
  columnas?: number;
}> = ({ selectedId, onPick, layout, columnas = 2 }) => {
  const { formatos, cargado, brandId } = useFormatosPersonalizados();
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [ancho, setAncho] = useState('1000');
  const [alto, setAlto] = useState('1500');
  const [archivo, setArchivo] = useState<FormatoDeSalida>('jpg');
  const [peso, setPeso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!cargado || !brandId) return null;

  const a = Number(ancho);
  const h = Number(alto);
  const p = peso.trim() ? Number(peso) : null;
  const medidasOk = Number.isFinite(a) && Number.isFinite(h) && a >= ANCHO_MIN && a <= ANCHO_MAX && h >= ANCHO_MIN && h <= ANCHO_MAX;
  const pesoOk = p === null || (Number.isFinite(p) && p >= PESO_MIN_KB && p <= PESO_MAX_KB);

  const guardar = async () => {
    if (!medidasOk) {
      setError(`El ancho y el alto van de ${ANCHO_MIN} a ${ANCHO_MAX} px.`);
      return;
    }
    if (!pesoOk) {
      setError(`El peso máximo va de ${PESO_MIN_KB} a ${PESO_MAX_KB} KB, o vacío para no ponerle tope.`);
      return;
    }
    setGuardando(true);
    setError(null);
    const formato = {
      ...nuevoPerfil(),
      nombre: nombre.trim().slice(0, 60),
      ancho: Math.round(a),
      alto: Math.round(h),
      formato: archivo,
      pesoMaxKB: p === null ? null : Math.round(p),
    };
    const r = await agregarFormatoPersonalizado(formato);
    setGuardando(false);
    if (!r.ok) {
      setError('No se pudo guardar el formato. Probá de nuevo.');
      return;
    }
    setCreando(false);
    setNombre('');
    onPick(slugDeFormatoPersonalizado(formato));
  };

  const tarjetas = formatos.map((f) => {
    const slug = slugDeFormatoPersonalizado(f);
    const isSel = selectedId === slug;
    const etiqueta = etiquetaDeFormatoPersonalizado(f);
    if (layout === 'chips') {
      return (
        <button
          key={f.id}
          type="button"
          role="radio"
          aria-checked={isSel}
          onClick={() => onPick(slug)}
          className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors ${isSel ? 'bg-[#1644EB0F] text-brand-primary ring-1 ring-brand-primary' : 'bg-neutral-50 text-[#02062BE6] ring-1 ring-transparent hover:bg-neutral-100'}`}
        >
          {etiqueta}
        </button>
      );
    }
    return layout === 'grid' ? (
      <button
        key={f.id}
        type="button"
        onClick={() => onPick(slug)}
        className={`relative text-left rounded-2xl overflow-hidden bg-white border-2 transition-all active:scale-[0.98] ${
          isSel ? 'border-brand-primary' : 'border-neutral-200'
        }`}
      >
        <div className="relative flex aspect-square items-center justify-center bg-neutral-100">
          <Proporcion ancho={f.ancho} alto={f.alto} lado={56} />
          {isSel && (
            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-brand-black/90 px-2.5 pt-2 font-medium truncate">{etiqueta}</p>
        <p className="text-[11px] text-[#3C3C43]/55 px-2.5 pb-2 truncate">{f.formato.toUpperCase()}</p>
      </button>
    ) : (
      <button
        key={f.id}
        type="button"
        onClick={() => onPick(slug)}
        className={`w-full text-left rounded-xl bg-white border p-3 flex items-center gap-4 transition-colors ${
          isSel ? 'border-brand-primary ring-2 ring-brand-primary/20' : 'border-neutral-200 hover:bg-neutral-50'
        }`}
      >
        <span className="w-16 h-16 rounded-md overflow-hidden shrink-0 flex items-center justify-center bg-neutral-100">
          <Proporcion ancho={f.ancho} alto={f.alto} lado={36} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14.5px] text-brand-black/90 font-medium truncate">{etiqueta}</span>
          <span className="block text-[12px] text-[#3C3C43]/55 truncate">{f.formato.toUpperCase()}</span>
        </span>
        {isSel && (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white shrink-0 ml-2">
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
        )}
      </button>
    );
  });

  const formulario = (
        <div className="rounded-xl border border-neutral-200 bg-white p-3.5">
          <div className="grid grid-cols-2 gap-2.5">
            <label className="col-span-2">
              <span className={ROTULO}>Nombre (opcional)</span>
              <input className={CAMPO} value={nombre} maxLength={60} placeholder="Ej: Tiendanube" onChange={(e) => setNombre(e.target.value)} />
            </label>
            <label>
              <span className={ROTULO}>Ancho (px)</span>
              <input className={CAMPO} inputMode="numeric" value={ancho} onChange={(e) => setAncho(e.target.value.replace(/\D/g, ''))} />
            </label>
            <label>
              <span className={ROTULO}>Alto (px)</span>
              <input className={CAMPO} inputMode="numeric" value={alto} onChange={(e) => setAlto(e.target.value.replace(/\D/g, ''))} />
            </label>
            <div className="col-span-2">
              <span className={ROTULO}>Archivo</span>
              <div className="flex gap-1.5">
                {ARCHIVOS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setArchivo(o.id)}
                    className={`flex-1 h-9 rounded-lg border text-[13px] font-medium transition-colors ${
                      archivo === o.id
                        ? 'border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-primary'
                        : 'border-neutral-200 bg-white text-brand-black/80 hover:bg-neutral-50'
                    }`}
                  >
                    {o.nombre}
                  </button>
                ))}
              </div>
            </div>
            <label className="col-span-2">
              <span className={ROTULO}>Peso máximo en KB (opcional)</span>
              <input className={CAMPO} inputMode="numeric" value={peso} placeholder="Sin tope" onChange={(e) => setPeso(e.target.value.replace(/\D/g, ''))} />
            </label>
          </div>
          {error && <p className="mt-2 text-[12px] text-red-600">{error}</p>}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setCreando(false)}
              className="flex-1 rounded-xl border border-neutral-200 bg-white py-2 px-4 text-[13.5px] font-semibold text-[#02062B]/[0.75] hover:bg-neutral-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void guardar()}
              disabled={guardando}
              className="flex-1 rounded-xl bg-brand-primary py-2 px-4 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {guardando ? 'Guardando…' : 'Guardar y usar'}
            </button>
          </div>
        </div>
  );

  if (layout === 'chips') {
    return (
      <div className="mt-1.5">
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Tus formatos">
          {tarjetas}
          {!creando && (
            <button
              type="button"
              onClick={() => {
                setCreando(true);
                setError(null);
              }}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12.5px] font-medium text-[#02062BE6] border border-dashed border-neutral-300 bg-white hover:bg-neutral-50"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
              Personalizado
            </button>
          )}
        </div>
        {creando && <div className="mt-2">{formulario}</div>}
      </div>
    );
  }

  return (
    <div className="mt-5">
      <p className="mb-2.5 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">Tus formatos</p>
      {tarjetas.length > 0 &&
        (layout === 'grid' ? (
          <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}>
            {tarjetas}
          </div>
        ) : (
          <div className="space-y-2.5 mb-3">{tarjetas}</div>
        ))}

      {!creando ? (
        <button
          type="button"
          onClick={() => {
            setCreando(true);
            setError(null);
          }}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-300 bg-white py-2.5 px-5 text-[13.5px] font-semibold text-[#02062B]/[0.75] transition-colors hover:bg-neutral-50"
        >
          <Plus className="h-4 w-4" strokeWidth={2.25} />
          Formato personalizado
        </button>
      ) : (
        formulario
      )}
    </div>
  );
};
